/**
 * Swatch Tray — Emily Daws Textiles
 * Phase 1 — bottom-anchored persistent tray on any page containing swatch
 * tiles (Swatch Shop, Grasscloth Swatches, Woven Swatches, mixed feature
 * collections, etc.), pop-and-drop on swatch PDPs, floating pill elsewhere
 * when the cart has unbought swatches.
 *
 * Source of truth: Shopify cart. Line items tagged with property `_swatch_tier`
 * are counted as tray items. Trade discounts are applied by Discount Depot —
 * we don't compute pricing here, just mirror what the cart returns.
 *
 * Prestige integration notes:
 *   • Suppress Prestige's `cart-notification` modal when adding a swatch by
 *     intercepting the publish of `cart-update` events for `_swatch_tier` items.
 *   • Re-fetch the tray section via Section Rendering API after every add/remove.
 *   • All DOM interactions are namespaced under `[data-swatch-*]` / `.swatch-*`
 *     to avoid stepping on Prestige class names.
 */

(function () {
  'use strict';

  // ---- Config ------------------------------------------------------------

  const SECTION_ID = 'swatch-tray';
  // Off-collection pop-and-drop duration. 3500ms = long enough for the
  // shopper to register the new swatch + count change, short enough not
  // to block scrolling.
  const POP_DURATION_MS = 3500;
  const STORAGE_KEY = 'edt:swatch-tray:state';

  // Tag that marks a product as a swatch (drives per-tile pip injection
  // and per-page tray docking). Every swatch product must carry this tag.
  const SWATCH_TAG = 'swatch';

  // The tray is persistently docked ONLY on these three collections.
  // Anywhere else, it surfaces briefly when Add Swatch is clicked, then
  // retreats. URL-based (not tile-based or cart-based) so the tray's
  // "home" is predictable.
  const STICKY_COLLECTION_HANDLES = new Set([
    'swatch-shop',
    'swatch-shop-all-fabrics',
    'all-wallpaper-swatches',
  ]);

  // Never show the tray on these paths — the real cart already covers them.
  const TRAY_FORBIDDEN_PATH_PREFIXES = ['/cart', '/checkout', '/account'];

  // ---- State -------------------------------------------------------------

  let popTimer = null;
  let isAdding = false;

  // Forward-declared helper: pip state sync (defined later, used by cart actions)
  const syncPipStatesSafe = () => {
    if (typeof window.__syncPipStates === 'function') return window.__syncPipStates();
    return Promise.resolve();
  };

  // ---- Helpers -----------------------------------------------------------

  const $ = (sel, root = document) => root.querySelector(sel);
  const $$ = (sel, root = document) => Array.from(root.querySelectorAll(sel));

  // Tray docks on the three swatch collection URLs ONLY. Elsewhere the
  // tray is hidden, with a brief pop-and-drop after Add Swatch clicks.
  const collectionHandleFromPath = () => {
    const m = /^\/collections\/([^/?#]+)/.exec(window.location.pathname);
    return m ? m[1] : null;
  };

  const onStickyPage = () => {
    const handle = collectionHandleFromPath();
    return handle ? STICKY_COLLECTION_HANDLES.has(handle) : false;
  };

  const onForbiddenPage = () => {
    const path = window.location.pathname;
    return TRAY_FORBIDDEN_PATH_PREFIXES.some((p) => path === p || path.startsWith(p + '/'));
  };

  // No-op kept so existing call sites don't break. Sticky state is now
  // pure URL math — nothing to invalidate.
  const invalidateStickyCache = () => {};

  const fetchSection = async () => {
    try {
      const res = await fetch(`${window.location.pathname}?sections=${SECTION_ID}`, {
        headers: { Accept: 'application/json' },
        credentials: 'same-origin',
      });
      if (!res.ok) throw new Error(`Section fetch ${res.status}`);
      const data = await res.json();
      return data[SECTION_ID];
    } catch (err) {
      console.warn('[SwatchTray] section refresh failed:', err);
      return null;
    }
  };

  const replaceTray = (html) => {
    if (!html) {
      console.warn('[SwatchTray] replaceTray called with empty html');
      return;
    }
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');
    const nextTray = doc.querySelector('#SwatchTray');
    const nextPill = doc.querySelector('#SwatchTrayPill');
    const currTray = document.getElementById('SwatchTray');
    const currPill = document.getElementById('SwatchTrayPill');

    // On sticky page (and not on cart/checkout/account), pre-clear `hidden`
    // on the incoming tray to avoid a flash.
    if (nextTray && onStickyPage() && !onForbiddenPage()) {
      nextTray.hidden = false;
      nextTray.removeAttribute('hidden');
    }

    // Preserve the user's collapse state across re-renders
    const wasCollapsed = currTray && currTray.classList.contains('is-collapsed');

    if (nextTray && currTray) {
      currTray.replaceWith(nextTray);
    } else {
      console.warn('[SwatchTray] tray nodes missing', { nextTray, currTray });
    }
    if (nextPill && currPill) currPill.replaceWith(nextPill);

    // Restore collapse state if it was collapsed
    if (wasCollapsed) {
      const newTray = document.getElementById('SwatchTray');
      const newBody = newTray && newTray.querySelector('[data-tray-body]');
      const newHandle = newTray && newTray.querySelector('[data-tray-handle]');
      if (newTray) newTray.classList.add('is-collapsed');
      if (newBody) newBody.hidden = true;
      if (newHandle) newHandle.setAttribute('aria-expanded', 'false');
    }

    bind();           // rebind to fresh DOM
    applyVisibility(); // re-evaluate which surface is shown
  };

  // Rebuild the tray content directly from /cart.js. This bypasses Shopify's
  // Section Rendering API (which has been flaky on this theme due to Splide
  // crashing the JS context on collection pages) and updates the tray in real
  // time using only the cart endpoint, which never fails.
  const moneyFormat = (cents) => {
    const fmt = (window.Shopify && window.Shopify.money_format) || '${{amount}}';
    const amount = (Number(cents) / 100).toFixed(2);
    return fmt.replace('{{amount}}', amount).replace('{{amount_no_decimals}}', Math.round(cents / 100));
  };

  const isTradeCustomer = () => {
    const tray = $('#SwatchTray');
    return tray && tray.dataset.isTrade === 'true';
  };

  const renderTrayFromCart = (cart) => {
    const tray = $('#SwatchTray');
    if (!tray) return;

    const swatchItems = cart.items.filter(
      (i) => i.properties && i.properties._swatch_tier
    );
    const count = swatchItems.length;
    const subtotal = swatchItems.reduce((s, i) => s + Number(i.final_line_price || 0), 0);
    const trade = isTradeCustomer();
    const displaySubtotal = trade ? 0 : subtotal;

    // Update tray-level data attribute (count drives pill / visibility logic)
    tray.dataset.count = String(count);

    // Header count text
    const countEl = tray.querySelector('[data-tray-count]');
    if (countEl) {
      countEl.textContent =
        count === 0 ? 'Empty' : count === 1 ? '1 swatch' : `${count} swatches`;
    }

    // Strip — empty state vs items
    const strip = tray.querySelector('[data-tray-strip]');
    if (strip) {
      if (count === 0) {
        strip.innerHTML =
          '<div class="swatch-tray__empty" data-tray-empty>Begin your tray \u2014 click + to add a swatch.</div>';
      } else {
        // Build a sized Shopify CDN URL. Shopify CDN accepts both `_160x.jpg`
        // (legacy) and `?width=160` (modern). The query param works on every
        // Files URL and is the safest bet for product images.
        const sizedUrl = (src) => {
          if (!src) return '';
          // Strip existing width/height query params, then append width=160
          const base = String(src).split('?')[0];
          return `${base}?width=160`;
        };

        const tilesHtml = swatchItems
          .map((item) => {
            const imgUrl = sizedUrl(item.image);
            const img = imgUrl
              ? `<img class="swatch-tray-tile__img" src="${imgUrl}" alt="${escapeHtml(item.product_title || '')}" loading="eager" decoding="async" width="80" height="80">`
              : '<div class="swatch-tray-tile__img swatch-tray-tile__img--blank" aria-hidden="true"></div>';
            return `<div class="swatch-tray-tile" data-tray-tile data-line-key="${item.key}">
              ${img}
              <button type="button" class="swatch-tray-tile__remove" data-tray-remove data-line-key="${item.key}" aria-label="Remove ${escapeHtml(item.product_title || '')} from tray">
                <svg width="9" height="9" viewBox="0 0 10 10" fill="none" aria-hidden="true">
                  <path d="M2 2l6 6M8 2L2 8" stroke="currentColor" stroke-width="1.3" stroke-linecap="round"/>
                </svg>
              </button>
            </div>`;
          })
          .join('');
        strip.innerHTML = tilesHtml;
      }
    }

    // Subtotal
    const subtotalEl = tray.querySelector('[data-tray-subtotal]');
    if (subtotalEl) subtotalEl.textContent = moneyFormat(displaySubtotal);

    // Note visibility
    const noteEl = tray.querySelector('[data-tray-note]');
    if (noteEl) noteEl.toggleAttribute('hidden', count === 0);

    // Move-to-Cart CTA removed from UX. Swatches are already cart line
    // items (added with `_swatch_tier` property), so the tray is just a
    // filtered cart view. Hide the legacy CTA element via JS so we don't
    // need a liquid edit.
    const cta = tray.querySelector('[data-tray-cta]');
    if (cta) {
      cta.hidden = true;
      cta.setAttribute('aria-hidden', 'true');
      cta.style.display = 'none';
    }

    // Pill count (for non-sticky pages)
    const pill = $('#SwatchTrayPill');
    if (pill) {
      const pillCount = pill.querySelector('[data-tray-pill-count]');
      if (pillCount) pillCount.textContent = String(count);
    }

    // Re-bind remove buttons that were just rendered
    bind();
    applyVisibility();
  };

  const escapeHtml = (s) =>
    String(s)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');

  const refreshTray = async () => {
    try {
      const res = await fetch('/cart.js', {
        credentials: 'same-origin',
        headers: { Accept: 'application/json' },
      });
      if (!res.ok) throw new Error(`cart fetch ${res.status}`);
      const cart = await res.json();
      renderTrayFromCart(cart);
      // Also directly update Prestige's <cart-count> custom elements
      // and <cart-dot> visibility — the Section Rendering API doesn't
      // always return <cart-count> in the header section (depends on
      // Prestige build), so this is the reliable path.
      try { updateCartCountElements(cart.item_count || 0); } catch (e) { /* noop */ }
    } catch (err) {
      console.warn('[SwatchTray] refreshTray (cart.js) failed:', err);
    }
  };

  // Write the cart item count directly into every <cart-count> element
  // on the page. Prestige-specific but harmless on other themes
  // (no-op when those elements aren't present).
  //
  // Note: we intentionally do NOT toggle <cart-dot>.is-visible here.
  // Prestige manages dot visibility via responsive CSS classes
  // (md:hidden / md-max:hidden) and forcing is-visible from JS leaks
  // the dot onto desktop where it shouldn't appear.
  const updateCartCountElements = (count) => {
    const n = Number(count) || 0;
    document.querySelectorAll('cart-count').forEach((el) => {
      el.textContent = String(n);
    });
    // Some Prestige builds also mirror the count into a sibling
    // [data-cart-count] node for accessibility — update those too.
    document.querySelectorAll('[data-cart-count]').forEach((el) => {
      el.textContent = String(n);
    });
  };

  // ---- Cart actions ------------------------------------------------------

  // Preload the swatch image from the product grid so the tray tile is
  // visually instant. We grab the existing <img> from inside the matching
  // product-card and stash its URL so renderTrayFromCart can reuse it as a
  // pre-warmed source (browser cache makes the second decode immediate).
  const preloadSwatchImage = (productHandle) => {
    if (!productHandle) return;
    const card = document.querySelector(`product-card[handle="${productHandle}"]`);
    if (!card) return;
    const cardImg = card.querySelector('img');
    if (cardImg && cardImg.src) {
      // Trigger a hidden preload — browser will cache the resized variant.
      const link = document.createElement('link');
      link.rel = 'preload';
      link.as = 'image';
      link.href = cardImg.src.split('?')[0] + '?width=160';
      document.head.appendChild(link);
      // Auto-cleanup after 30s so we don't leak preload tags
      setTimeout(() => link.remove(), 30000);
    }
  };

  // ---- Header + cart-drawer live refresh --------------------------------
  //
  // After a swatch is added or removed from the cart, we use Shopify's
  // Section Rendering API to fetch fresh HTML for the header and cart
  // drawer, then surgically replace those nodes in place. This is the
  // official Shopify pattern and doesn't involve Prestige's pubsub at
  // all — so it can't trigger the cart-notification overlay or the
  // "blank page with a number" rendering bug.
  //
  // Sections refreshed:
  //   • header        — picks up the cart icon bubble count
  //   • cart-drawer   — picks up line items / subtotal / empty state
  //
  // We replace by id when we can (Prestige typically gives the header
  // and drawer stable ids) and fall back to first-child swap otherwise.

  const refreshCartSections = async () => {
    try {
      const res = await fetch('/?sections=header,cart-drawer', {
        credentials: 'same-origin',
        headers: { Accept: 'application/json' },
      });
      if (!res.ok) return;
      const sections = await res.json();
      const parser = new DOMParser();

      // ---- Header refresh ----
      // We search the WHOLE fetched header HTML for cart-count/cart-dot
      // elements and replace every matching element on the live page.
      // Searching globally (instead of only inside a matched header
      // container) is more robust to Prestige variants where the header
      // wrapper id differs from build to build.
      if (sections.header) {
        const doc = parser.parseFromString(sections.header, 'text/html');
        // Note: we intentionally omit 'cart-dot' here. Its visibility is
        // controlled by Prestige's responsive CSS classes and replacing
        // the element from the section-rendered HTML can leak the dot
        // onto the desktop "Cart (N)" label.
        const bubbleSelectors = [
          'cart-count',              // Prestige custom element — primary
          '[data-cart-count]',
          '.cart-count-bubble',
          '.header__cart-count',
          '.cart-count',
          '[data-header-cart-count]',
        ];
        for (const bSel of bubbleSelectors) {
          const nextEls = Array.from(doc.querySelectorAll(bSel));
          const currEls = Array.from(document.querySelectorAll(bSel));
          if (!nextEls.length || !currEls.length) continue;
          // Pair up by position; if counts differ, replace what we can.
          const pairs = Math.min(nextEls.length, currEls.length);
          for (let i = 0; i < pairs; i++) {
            try { currEls[i].replaceWith(nextEls[i].cloneNode(true)); } catch (e) { /* noop */ }
          }
        }
      }

      // ---- Cart drawer refresh ----
      if (sections['cart-drawer']) {
        const doc = parser.parseFromString(sections['cart-drawer'], 'text/html');
        const candidates = [
          '#shopify-section-cart-drawer',
          '[data-section-id="cart-drawer"]',
          'cart-drawer',
          '.cart-drawer',
          '#CartDrawer',
        ];
        for (const sel of candidates) {
          const next = doc.querySelector(sel);
          const curr = document.querySelector(sel);
          if (next && curr) {
            // Preserve open/closed state across the swap so we don't
            // accidentally open the drawer when the user is browsing.
            const wasOpen =
              curr.classList.contains('is-open') ||
              curr.classList.contains('open') ||
              curr.getAttribute('aria-hidden') === 'false';
            curr.replaceWith(next.cloneNode(true));
            if (wasOpen) {
              const fresh = document.querySelector(sel);
              if (fresh) {
                fresh.classList.add('is-open');
                fresh.setAttribute('aria-hidden', 'false');
              }
            }
            break;
          }
        }
      }
    } catch (e) { /* silent — not worth disturbing the user over */ }
  };

  const addToCart = async ({ variantId, tier, productHandle, popOnAdd }) => {
    if (isAdding) return;
    isAdding = true;

    // Optimistic pip flip
    const pip = $(`[data-tray-add][data-variant-id="${variantId}"]`);
    if (pip) {
      pip.setAttribute('aria-pressed', 'true');
      pip.setAttribute('aria-label', 'Remove from selection');
      if (pip.classList.contains('swatch-pdp-add')) {
        pip.dataset.labelAdd = pip.dataset.labelAdd || pip.textContent;
        pip.textContent = 'Added to Tray';
      }
    }

    // Preload the swatch thumbnail so it's ready when the tray rerenders
    preloadSwatchImage(productHandle);

    try {
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify({
          items: [
            {
              id: Number(variantId),
              quantity: 1,
              properties: {
                _swatch_tier: tier,
                _swatch_source: productHandle || 'swatch-shop',
              },
            },
          ],
        }),
      });

      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.description || `Add failed (${res.status})`);
      }

      // Suppress Prestige's native cart drawer/notification for this add.
      // We do this by NOT publishing 'cart-update' through theme.pubsub.
      // Instead we refresh just the tray section.
      //
      // IMPORTANT: each post-add step is wrapped in its own try/catch so a
      // failure in one (e.g. Prestige's broken splide.min.js poisoning the
      // page) cannot stop the others.
      try { await refreshTray(); } catch (e) { console.warn('[SwatchTray] refreshTray failed', e); }
      try { await syncPipStatesSafe(); } catch (e) { console.warn('[SwatchTray] syncPipStates failed', e); }

      // Refresh header cart bubble + cart drawer via Section Rendering API.
      try { await refreshCartSections(); } catch (e) { /* noop */ }
      try { bumpHeaderCartCount(); } catch (e) { /* noop */ }

      // Off-collection pop-and-drop. Any Add Swatch click outside the
      // three sticky collections surfaces the tray briefly then retreats.
      if (!onStickyPage()) {
        popThenDrop();
      }
    } catch (err) {
      console.error('[SwatchTray] add failed:', err);
      if (pip) pip.setAttribute('aria-pressed', 'false');
      // Best-effort user-facing toast
      flashError('Could not add swatch. Please try again.');
    } finally {
      isAdding = false;
    }
  };

  const removeFromCart = async (lineKey, variantIdHint) => {
    // Optimistic UI: flip the matching pip back to + immediately, and remove
    // the tile from the tray strip before the server responds.
    if (variantIdHint) {
      const pip = $(`[data-tray-add][data-variant-id="${variantIdHint}"]`);
      if (pip) {
        pip.setAttribute('aria-pressed', 'false');
        pip.setAttribute('aria-label', 'Add to selection');
        if (pip.classList.contains('swatch-pdp-add') && pip.dataset.labelAdd) {
          pip.textContent = pip.dataset.labelAdd;
        }
      }
    }
    const tile = $(`[data-tray-tile][data-line-key="${lineKey}"]`);
    if (tile) tile.style.opacity = '0.4';

    try {
      // STALE-KEY GUARD:
      //   Line keys in Shopify carts change whenever the cart is mutated
      //   from another surface (the native cart drawer, the cart page, or
      //   a quantity tweak). If our tray was rendered before such a
      //   mutation, the key on the tile is stale and /cart/change.js will
      //   return 400 "no valid id or line parameter." We've seen this
      //   exact failure in production.
      //
      //   Fix: before removing, re-fetch the cart and resolve a CURRENT
      //   line key. We resolve by:
      //     1. exact key match (best case — nothing changed)
      //     2. variant_id + has _swatch_tier (handles re-keying)
      //   If neither resolves, the line is already gone — treat as success
      //   and just refresh the tray so the UI catches up.
      //
      //   We also use /cart/update.js (not change.js) because update.js
      //   is forgiving about stale keys (treats them as no-ops) and lets
      //   us batch in one round trip.
      let currentKey = String(lineKey);
      let resolved = false;
      try {
        const cartRes = await fetch('/cart.js', { credentials: 'same-origin' });
        if (cartRes.ok) {
          const cart = await cartRes.json();
          const exact = cart.items.find((i) => i.key === currentKey);
          if (exact) {
            resolved = true;
          } else if (variantIdHint) {
            const byVariant = cart.items.find(
              (i) =>
                String(i.variant_id) === String(variantIdHint) &&
                i.properties &&
                i.properties._swatch_tier
            );
            if (byVariant) {
              currentKey = byVariant.key;
              resolved = true;
            }
          }
          // If neither match — line already gone. Refresh and exit cleanly.
          if (!resolved) {
            try { await refreshTray(); } catch (e) { /* noop */ }
            try { await syncPipStatesSafe(); } catch (e) { /* noop */ }
            try { await refreshCartSections(); } catch (e) { /* noop */ }
            try { bumpHeaderCartCount(); } catch (e) { /* noop */ }
            return;
          }
        }
      } catch (e) { /* fall through to update.js attempt with original key */ }

      const res = await fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify({ updates: { [currentKey]: 0 } }),
      });
      if (!res.ok) throw new Error(`Remove failed (${res.status})`);
      try { await refreshTray(); } catch (e) { console.warn('[SwatchTray] post-remove refresh failed', e); }
      try { await syncPipStatesSafe(); } catch (e) { /* noop */ }
      try { await refreshCartSections(); } catch (e) { /* noop */ }
      try { bumpHeaderCartCount(); } catch (e) { /* noop */ }
    } catch (err) {
      console.error('[SwatchTray] remove failed:', err);
      // Revert optimistic UI on failure
      if (tile) tile.style.opacity = '1';
      flashError('Could not remove swatch.');
    }
  };

  // ---- Visibility orchestration -----------------------------------------

  const applyVisibility = () => {
    const tray = $('#SwatchTray');
    const pill = $('#SwatchTrayPill');
    if (!tray || !pill) return;

    // Hide everywhere the real cart already covers.
    if (onForbiddenPage()) {
      tray.hidden = true;
      pill.hidden = true;
      return;
    }

    // Defensive: hide the legacy Move-to-Cart CTA every time we evaluate
    // visibility (covers the empty-cart-on-load case where renderTrayFromCart
    // hasn't fired yet).
    const cta = tray.querySelector('[data-tray-cta]');
    if (cta) {
      cta.hidden = true;
      cta.setAttribute('aria-hidden', 'true');
      cta.style.display = 'none';
    }

       if (onStickyPage()) {
      // Always show on the three swatch collections, fully expanded.
      // Customers are actively shopping swatches — give them room.
      tray.hidden = false;
      tray.classList.remove('is-collapsed');
      const stickyBody = tray.querySelector('[data-tray-body]');
      const stickyHandle = tray.querySelector('[data-tray-handle]');
      if (stickyBody) stickyBody.hidden = false;
      if (stickyHandle) stickyHandle.setAttribute('aria-expanded', 'true');
      pill.hidden = true;
      return;
    }

    // Off-collection: tray docks everywhere (except forbidden paths)
    // as a collapsed header-only bar at the bottom. Clicking the header
    // expands it. External adds (e.g. Section Store upsell) trigger a
    // 3.5s peek-expand via popThenDrop().
    tray.hidden = false;
    pill.hidden = true;

    // Default to collapsed on non-sticky pages unless we're mid-pop
    // OR the user already manually expanded it this session.
    const sessionState = sessionStorage.getItem(STORAGE_KEY);
    const userExpanded = sessionState === 'expanded';
    const isPopped = tray.classList.contains('is-popped');

    if (!isPopped && !userExpanded) {
      tray.classList.add('is-collapsed');
      const body = tray.querySelector('[data-tray-body]');
      const handle = tray.querySelector('[data-tray-handle]');
      if (body) body.hidden = true;
      if (handle) handle.setAttribute('aria-expanded', 'false');
    }
  };

  const popThenDrop = () => {
    const tray = $('#SwatchTray');
    if (!tray) return;
    if (onForbiddenPage()) return;
    if (popTimer) clearTimeout(popTimer);

    tray.hidden = false;
    tray.classList.add('is-popped');

    // Expand the body for the peek (works whether the tray was previously
    // collapsed or already expanded).
    const body = tray.querySelector('[data-tray-body]');
    const handle = tray.querySelector('[data-tray-handle]');
    const wasCollapsed = tray.classList.contains('is-collapsed');
    if (wasCollapsed) {
      tray.classList.remove('is-collapsed');
      if (body) body.hidden = false;
      if (handle) handle.setAttribute('aria-expanded', 'true');
    }

    requestAnimationFrame(() => tray.classList.add('is-visible'));

    popTimer = setTimeout(() => {
      tray.classList.remove('is-visible');
      setTimeout(() => {
        tray.classList.remove('is-popped');
        // On sticky pages, tray stays fully expanded.
        // On non-sticky pages, retreat back to collapsed header-only.
        if (!onStickyPage()) {
          tray.classList.add('is-collapsed');
          if (body) body.hidden = true;
          if (handle) handle.setAttribute('aria-expanded', 'false');
        }
      }, 320);
    }, POP_DURATION_MS);
  };

  // ---- External add hook -------------------------------------------------
  // Listens for swatch adds from sources outside our control (e.g. the
  // Section Store "ss_upsell_01" section on fabric/wallpaper PDPs). The
  // emitter is responsible for calling /cart/add.js itself; we just react.
  const handleExternalAdd = async () => {
    try {
      // Refresh tray content from cart
      const res = await fetch('/cart.js', { credentials: 'same-origin' });
      if (res.ok) {
        const cart = await res.json();
        renderTrayFromCart(cart);
      }
      // Refresh header count + drawer
      if (typeof refreshCartSections === 'function') refreshCartSections();
      bumpHeaderCartCount();
      // Sync pip states in case the added swatch is also visible on this page
      syncPipStatesSafe();
    } catch (e) {
      console.warn('[SwatchTray] external-add refresh failed:', e);
    }
    // Peek the tray (auto-retreats after POP_DURATION_MS)
    popThenDrop();
  };

  const togglePillToTray = () => {
    const tray = $('#SwatchTray');
    const pill = $('#SwatchTrayPill');
    if (!tray || !pill) return;
    tray.hidden = false;
    tray.classList.add('is-popped', 'is-visible');
    pill.hidden = true;
    // No auto-drop when user explicitly opened it.
    if (popTimer) {
      clearTimeout(popTimer);
      popTimer = null;
    }
  };

  // ---- Tray handle (collapse / expand) ----------------------------------

  const wireHandle = () => {
    const handle = $('[data-tray-handle]');
    const body = $('[data-tray-body]');
    if (!handle || !body) return;

    const persisted = sessionStorage.getItem(STORAGE_KEY);
    if (persisted === 'collapsed') {
      body.hidden = true;
      handle.setAttribute('aria-expanded', 'false');
      const tray = $('#SwatchTray');
      if (tray) tray.classList.add('is-collapsed');
    }
    if (handle.__swatchHandleBound) return;
    handle.__swatchHandleBound = true;
    const toggle = () => {
      const expanded = handle.getAttribute('aria-expanded') === 'true';
      handle.setAttribute('aria-expanded', String(!expanded));
      body.hidden = expanded;
      const tray = $('#SwatchTray');
      if (tray) tray.classList.toggle('is-collapsed', expanded);
      sessionStorage.setItem(STORAGE_KEY, expanded ? 'collapsed' : 'expanded');
    };

    handle.addEventListener('click', toggle);
    handle.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        toggle();
      }
    });
  };

  // ---- Header cart count (best-effort, Prestige-friendly) ---------------

  const bumpHeaderCartCount = async () => {
    try {
      const res = await fetch('/cart.js', { credentials: 'same-origin' });
      if (!res.ok) return;
      const cart = await res.json();
      // Prestige uses [data-cart-count] in the header.
      $$('[data-cart-count], .header__cart-count, .cart-count-bubble').forEach((el) => {
        el.textContent = cart.item_count;
        el.toggleAttribute('hidden', cart.item_count === 0);
      });
    } catch (e) { /* noop */ }
  };

  // ---- Lightweight error toast ------------------------------------------

  const flashError = (msg) => {
    let toast = document.getElementById('SwatchTrayToast');
    if (!toast) {
      toast = document.createElement('div');
      toast.id = 'SwatchTrayToast';
      toast.className = 'swatch-tray-toast';
      toast.setAttribute('role', 'status');
      document.body.appendChild(toast);
    }
    toast.textContent = msg;
    toast.classList.add('is-visible');
    setTimeout(() => toast.classList.remove('is-visible'), 2400);
  };

  // ---- Event binding -----------------------------------------------------

  const bind = () => {
    // Add buttons (grid pips + PDP buttons)
    $$('[data-tray-add]').forEach((btn) => {
      if (btn.__swatchBound) return;
      btn.__swatchBound = true;
      // Intercept BEFORE Prestige's product-card click handler can fire.
      // Use capture phase + stopImmediatePropagation to block navigation.
      const blockNav = (e) => {
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();
      };
      btn.addEventListener('mousedown', blockNav, true);
      btn.addEventListener('pointerdown', blockNav, true);
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();
        const pressed = btn.getAttribute('aria-pressed') === 'true';
        const variantId = btn.dataset.variantId;
        const tier = btn.dataset.tier || 'price-10';
        const handle = btn.dataset.productHandle;
        const popOnAdd = btn.dataset.popOnAdd === 'true' || btn.closest('[data-swatch-tile]');

             if (pressed) {
          // Already in tray — flip the pip back optimistically, then
          // look up the line key and remove. The cart.js round trip is
          // unavoidable but the UI is already responsive.
          btn.setAttribute('aria-pressed', 'false');
          btn.setAttribute('aria-label', 'Add to selection');
          if (btn.classList.contains('swatch-pdp-add') && btn.dataset.labelAdd) {
            btn.textContent = btn.dataset.labelAdd;
          }
          fetch('/cart.js', { credentials: 'same-origin' })
            .then((r) => r.json())
            .then((cart) => {
              const line = cart.items.find(
                (i) => String(i.variant_id) === String(variantId) && i.properties && i.properties._swatch_tier
              );
              if (line) removeFromCart(line.key, variantId);
            })
            .catch(() => {
              // Revert pip on failure
              btn.setAttribute('aria-pressed', 'true');
              btn.setAttribute('aria-label', 'Remove from selection');
            });
        } else {
          addToCart({ variantId, tier, productHandle: handle, popOnAdd });
        }
      });
    });

    // Remove buttons inside the tray strip
    $$('[data-tray-remove]').forEach((btn) => {
      if (btn.__swatchBound) return;
      btn.__swatchBound = true;
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();
        const key = btn.dataset.lineKey;
        if (!key) return;
        // Look up variant id from cart so we can flip the matching grid pip
        fetch('/cart.js', { credentials: 'same-origin' })
          .then((r) => r.json())
          .then((cart) => {
            const line = cart.items.find((i) => i.key === key);
            removeFromCart(key, line && line.variant_id);
          })
          .catch(() => removeFromCart(key));
      });
    });

    // Floating pill → open tray
    const pill = $('[data-tray-pill]');
    if (pill && !pill.__swatchBound) {
      pill.__swatchBound = true;
      pill.addEventListener('click', togglePillToTray);
    }

    // Handle (collapse/expand)
    wireHandle();
  };

  // ---- Pip injection (Swatch Shop collection only) ----------------------
  //
  // Prestige renders each tile as <product-card handle="...">. We:
  //   1. Scope to /collections/swatch-shop only.
  //   2. For each <product-card> we haven't touched yet, fetch
  //      /products/{handle}.js once to read first variant id + tags.
  //   3. Derive tier from `price-NN` tag (e.g. price-10, price-15, price-20).
  //   4. Inject a + pip button into .product-card__figure.
  //   5. Pre-flip pip to 'x' if that variant is already in the cart.
  //
  // We cache product data per-handle in memory and persist for the session
  // so repeated grid renders / infinite scroll don't refetch.

  const PIP_CACHE = {};
  let inCartVariantIds = new Set();

  // Tier detection — reads the `price-NN` tag scheme.
  //
  // Every swatch product is tagged with one `price-NN` tag where NN is the
  // retail price in whole dollars (e.g. price-10, price-15, price-20, price-25).
  // The tag value itself (e.g. "price-10") becomes the `_swatch_tier` line
  // item property — Discount Depot matches on this property to zero out
  // Trade customers. Any new price point just needs a `price-NN` tag on the
  // product; no code change required.
  //
  // Legacy fallback: if a product is missing a price-NN tag, we fall back
  // to the old material-word scheme (fabric/clay-coated/grasscloth) so the
  // grid never breaks during a partial re-tag.
  const tierFromTags = (tags) => {
    if (!Array.isArray(tags)) return 'price-10';
    const lower = tags.map((t) => String(t).toLowerCase().trim());

    // Preferred: explicit price-NN tag
    const priceTag = lower.find((t) => /^price-\d+$/.test(t));
    if (priceTag) return priceTag;

    // Legacy fallback (in case any product missed re-tagging)
    if (lower.includes('grasscloth')) return 'price-20';
    if (
      lower.includes('clay-coated') ||
      lower.includes('clay coated') ||
      lower.includes('clay_coated') ||
      lower.includes('claycoated')
    ) return 'price-15';
    if (lower.includes('fabric')) return 'price-10';

    // Final default: treat as cheapest tier rather than free
    return 'price-10';
  };

  const fetchProductMeta = async (handle) => {
    if (PIP_CACHE[handle]) return PIP_CACHE[handle];
    try {
      const res = await fetch(`/products/${handle}.js`, { credentials: 'same-origin' });
      if (!res.ok) throw new Error(`product fetch ${res.status}`);
      const p = await res.json();
      const firstVariant = (p.variants && p.variants[0]) || null;
      const tagsLower = Array.isArray(p.tags) ? p.tags.map((t) => String(t).toLowerCase().trim()) : [];
      const meta = {
        variantId: firstVariant ? firstVariant.id : null,
        available: firstVariant ? firstVariant.available : false,
        tier: tierFromTags(p.tags),
        // True iff product carries the `swatch` tag. Used to decide whether
        // to inject a + pip on the tile and whether the tray should dock
        // on the current page.
        isSwatch: tagsLower.includes(SWATCH_TAG),
      };
      PIP_CACHE[handle] = meta;
      return meta;
    } catch (err) {
      console.warn(`[SwatchTray] meta fetch failed for ${handle}`, err);
      return null;
    }
  };

  const refreshInCartSet = async () => {
    try {
      const res = await fetch('/cart.js', { credentials: 'same-origin' });
      if (!res.ok) return;
      const cart = await res.json();
      inCartVariantIds = new Set(
        cart.items
          .filter((i) => i.properties && i.properties._swatch_tier)
          .map((i) => String(i.variant_id))
      );
    } catch (e) { /* noop */ }
  };

  const buildPip = ({ variantId, tier, handle, pressed }) => {
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'swatch-pip';
    btn.setAttribute('data-tray-add', '');
    btn.setAttribute('data-variant-id', variantId);
    btn.setAttribute('data-tier', tier);
    btn.setAttribute('data-product-handle', handle);
    btn.setAttribute('aria-pressed', pressed ? 'true' : 'false');
    btn.setAttribute('aria-label', pressed ? 'Remove from selection' : 'Add to selection');
    btn.innerHTML = '<span class="swatch-pip__icon" aria-hidden="true"></span>';
    return btn;
  };

  const injectPips = async () => {
    // Per-tile detection: scan every product-card on the page and inject
    // a pip only on swatch tiles. Works on any collection (Swatch Shop,
    // Grasscloth Swatches, Woven Swatches, mixed feature collections, etc.)
    // and is safe on pages with no swatches at all (it just does nothing).
    const cards = $$('product-card[handle]').filter((c) => !c.__swatchPipBound);
    if (cards.length === 0) return;

    // Ensure cart state is current before stamping pips
    await refreshInCartSet();

    let pipInjectedThisRun = false;

    // Fetch metas in parallel (modest concurrency — browsers cap themselves)
    await Promise.all(
      cards.map(async (card) => {
        card.__swatchPipBound = true;
        const handle = card.getAttribute('handle');
        if (!handle) return;
        const meta = await fetchProductMeta(handle);
        if (!meta || !meta.variantId) return;

        // Skip non-swatch products — no pip on wallpaper/yardage/finished goods.
        if (!meta.isSwatch) return;

        // Container — Prestige's .product-card__figure is the image well
        const figure = card.querySelector('.product-card__figure') || card;
        // Avoid double-inject
        if (figure.querySelector('.swatch-pip')) return;

        const pressed = inCartVariantIds.has(String(meta.variantId));
        const pip = buildPip({
          variantId: meta.variantId,
          tier: meta.tier,
          handle,
          pressed,
        });

        // Ensure figure is positioned for absolute pip
        if (getComputedStyle(figure).position === 'static') {
          figure.style.position = 'relative';
        }
        figure.appendChild(pip);
        card.setAttribute('data-swatch-pip-injected', 'true');
        pipInjectedThisRun = true;
      })
    );

    // If we injected at least one pip on this page, the page is now
    // "sticky" — invalidate the cache and re-evaluate tray visibility so
    // the bottom dock can appear.
    if (pipInjectedThisRun) {
      invalidateStickyCache();
      try { applyVisibility(); } catch (e) { /* noop */ }
    }

    // Bind newly-injected pips to add/remove handlers
    bind();
  };

  // Watch for Prestige re-rendering the grid (filters, infinite scroll, etc.).
  // Runs on every page that has a product-list; injectPips() itself is the
  // gatekeeper that filters down to swatch tiles.
  //
  // IMPORTANT: when a filter is applied, Prestige replaces the entire
  // <product-list> element wholesale — not just its children. If we observe
  // the list element directly, the observer dies silently when the element
  // is removed from the DOM. So we observe the list's PARENT (or body as a
  // fallback) with subtree:true so we catch:
  //   1. New tiles added inside an existing product-list (infinite scroll)
  //   2. Whole product-list replaced (filter / sort changes)
  //   3. Brand-new product-list inserted (collection re-renders)
  let pipObserver = null;
  let pipObserverTarget = null;
  const watchGrid = () => {
    const list = $('product-list') || $('.product-list');
    // Prefer the parent of product-list; fall back to <main> or <body>.
    const target =
      (list && list.parentNode) ||
      document.querySelector('main') ||
      document.body;
    if (!target) return;
    // If we're already observing this exact node, no-op.
    if (pipObserver && pipObserverTarget === target) return;
    // Otherwise (re)attach.
    if (pipObserver) {
      try { pipObserver.disconnect(); } catch (e) { /* noop */ }
    }
    pipObserver = new MutationObserver(() => {
      // Debounce via microtask — let Prestige finish writes first
      queueMicrotask(() => {
        try { injectPips(); } catch (e) { /* noop */ }
        // If product-list was replaced, our observer target may now be stale.
        // Re-evaluate on next tick to rebind to the new parent if needed.
        const currentList = $('product-list') || $('.product-list');
        const currentParent = currentList && currentList.parentNode;
        if (currentParent && currentParent !== pipObserverTarget) {
          try { watchGrid(); } catch (e) { /* noop */ }
        }
      });
    });
    pipObserver.observe(target, { childList: true, subtree: true });
    pipObserverTarget = target;
  };

  // ---- Swatch PDP rewire ------------------------------------------------
  //
  // On swatch PDPs we:
  //   1. Detect we're on a swatch product (by reading the product handle from
  //      the URL and checking its `swatch` tag via /products/{handle}.js)
  //   2. Replace the native Prestige Add to Cart button label with
  //      "Add Swatch — $X" (guest) or "Add Swatch" (Trade)
  //   3. Route clicks through our tray-add flow (sets _swatch_tier property,
  //      triggers pop-and-drop, skips Prestige's cart-notification modal)
  //   4. Hide the quantity selector — swatches are 1-per by design
  //   5. Insert a calm helper line above the button to explain the model
  //      to first-time visitors who don't know what the "selections" list is
  //
  // Detection runs on every URL change too (in case Prestige soft-navigates
  // between PDPs without a full page reload).

  const PDP_REWIRE_FLAG = '__swatchPdpRewired';

  // Money formatter — returns "$10" / "$15" style (no decimals if even).
  const formatTierPrice = (priceCents) => {
    const dollars = priceCents / 100;
    return Number.isInteger(dollars) ? `$${dollars}` : `$${dollars.toFixed(2)}`;
  };

  // Derive cents from a tier tag like "price-10" — returns 1000.
  const centsFromTier = (tier) => {
    const m = /^price-(\d+(?:\.\d+)?)$/.exec(tier || '');
    if (!m) return null;
    return Math.round(parseFloat(m[1]) * 100);
  };

  // Are we on a PDP? Shopify uses /products/{handle} URLs.
  const pdpHandleFromUrl = () => {
    const m = /^\/products\/([^/?#]+)/.exec(window.location.pathname);
    return m ? m[1] : null;
  };

  // (isTradeCustomer is defined earlier in the file — reused here.)

  // Find the native Prestige Add-to-Cart button on the current PDP.
  // Prestige uses several patterns — we cast a wide net.
  const findNativeAddButton = () => {
    return (
      document.querySelector('product-form button[type="submit"][name="add"]') ||
      document.querySelector('form[action*="/cart/add"] button[type="submit"]') ||
      document.querySelector('[data-product-form] button[type="submit"]') ||
      null
    );
  };

  // Find the quantity selector wrapper on the PDP.
  const findQuantitySelector = () => {
    return (
      document.querySelector('quantity-input') ||
      document.querySelector('.product-form__quantity') ||
      document.querySelector('[data-product-form] .quantity-selector') ||
      null
    );
  };

  // The helper line element — we insert this once above the button.
  const buildHelperLine = () => {
    const p = document.createElement('p');
    p.className = 'swatch-pdp-helper';
    p.setAttribute('data-swatch-pdp-helper', '');
    p.textContent =
      "Add swatches to your list below, then move them to your cart when you're ready. Shipping is calculated once at checkout.";
    return p;
  };

  const rewireSwatchPdp = async () => {
    const handle = pdpHandleFromUrl();
    if (!handle) return;

    // Avoid double-rewire on soft nav back to same PDP
    if (document.body.dataset[PDP_REWIRE_FLAG] === handle) return;
    const meta = await fetchProductMeta(handle);
    if (!meta || !meta.isSwatch || !meta.variantId) return;

    const nativeBtn = findNativeAddButton();
    if (!nativeBtn) return; // No native button found; bail safely

    // Mark body so we don't rewire twice for the same handle
    document.body.dataset[PDP_REWIRE_FLAG] = handle;

    // 1. Compute new label
    const trade = isTradeCustomer();
    const cents = centsFromTier(meta.tier);
    let label;
    if (trade) {
      label = 'Add Swatch';
    } else if (cents !== null) {
      label = `Add Swatch \u2014 ${formatTierPrice(cents)}`;
    } else {
      label = 'Add Swatch';
    }

    // 2. Convert native button to a tray-add button.
    //    Strategy: hijack in-place — keep the existing DOM (preserves
    //    Prestige's styling), just swap text + data attrs + add the
    //    [data-tray-add] hook that our bind() loop already understands.
    //    We also stop the native form from submitting via a capture-phase
    //    listener that lives on the button.
    nativeBtn.textContent = label;
    nativeBtn.dataset.labelAdd = label;        // ← NEW: save label for restore on remove
    nativeBtn.setAttribute('type', 'button'); // detach from <form> submit
    nativeBtn.setAttribute('data-tray-add', '');
    nativeBtn.setAttribute('data-variant-id', String(meta.variantId));
    nativeBtn.setAttribute('data-tier', meta.tier);
    nativeBtn.setAttribute('data-product-handle', handle);
    nativeBtn.setAttribute('data-pop-on-add', 'true');
    // Mirror the pip's pressed state if this swatch is already in the list
    const already = inCartVariantIds.has(String(meta.variantId));
    nativeBtn.setAttribute('aria-pressed', already ? 'true' : 'false');
    nativeBtn.setAttribute('aria-label', already ? 'Remove from selection' : 'Add to selection');
    nativeBtn.classList.add('swatch-pdp-add');
    if (already) nativeBtn.textContent = 'Added to Tray'; // ← NEW: correct label on page load

    // Block the form from submitting if user double-clicks before bind()
    // attaches its handler. The bind() loop will then add the real handler.
    const blockSubmit = (e) => {
      e.preventDefault();
      e.stopPropagation();
      e.stopImmediatePropagation();
    };
    if (!nativeBtn.__swatchPdpBlocked) {
      nativeBtn.__swatchPdpBlocked = true;
      nativeBtn.addEventListener('mousedown', blockSubmit, true);
      nativeBtn.addEventListener('pointerdown', blockSubmit, true);
    }

    // 3. Hide the quantity selector (swatches are 1-per by design)
    const qty = findQuantitySelector();
    if (qty) {
      qty.style.display = 'none';
      qty.setAttribute('data-swatch-hidden', 'true');
    }

    // 4. Insert helper line above the button (if not already inserted)
    if (!document.querySelector('[data-swatch-pdp-helper]')) {
      const helper = buildHelperLine();
      const insertionParent = nativeBtn.closest('product-form') || nativeBtn.parentNode;
      if (insertionParent && nativeBtn.parentNode) {
        nativeBtn.parentNode.insertBefore(helper, nativeBtn);
      }
    }

    // 5. Bind our handler now that data-tray-add is in place
    try { bind(); } catch (e) { /* noop */ }
  };

  // Run PDP rewire after init + after every soft-nav
  const tryRewirePdp = () => {
    try { rewireSwatchPdp(); } catch (e) { console.warn('[SwatchTray] PDP rewire failed', e); }
  };

  // ---- Mini-ATC upsell hijack -------------------------------------------
  //
  // The Section Store "ss-upsell" / Mini ATC app renders a cross-sell
  // swatch card on yardage / wallpaper PDPs into <div id="mini-atc-target">.
  // Its built-in Add button fires the cart directly, bypassing our list.
  //
  // We can't edit the third-party app, but we CAN observe the injection
  // point and intercept the click after the app renders. We:
  //   1. Watch #mini-atc-target for child mutations (the app injects async)
  //   2. When a button appears inside, find the linked swatch handle
  //      (the app puts a product URL in a nearby <a> or the button's href)
  //   3. Hijack the button: prevent default, route through our addToCart
  //      flow with the correct tier, trigger pop-and-drop
  //   4. Update the button label to "Add Swatch — $X" / "Add Swatch" (Trade)
  //
  // Because the app's HTML structure may change, we use defensive selectors
  // and bail safely if anything's unexpected.

  const MINI_ATC_FLAG = '__swatchMiniAtcBound';

  // Try to find the swatch product handle that the upsell card links to.
  // Strategy: look for an <a href="/products/..."> inside the target div.
  const findMiniAtcSwatchHandle = (container) => {
    const link = container.querySelector('a[href*="/products/"]');
    if (!link) return null;
    const m = /\/products\/([^/?#]+)/.exec(link.getAttribute('href') || '');
    return m ? m[1] : null;
  };

  // Find the Add button inside the upsell card. The app typically uses
  // a <button> with an "add" / "Add" / price-bearing label, OR an <a>
  // styled as a button. We grab the first interactive element that
  // isn't the product-image link.
  const findMiniAtcAddButton = (container) => {
    return (
      // Current Section Store ss_upsell_01 structure (2026)
      container.querySelector('[data-mini-add-to-cart]') ||
      container.querySelector('button[type="submit"]') ||
      container.querySelector('button[data-add]') ||
      container.querySelector('button.add-to-cart') ||
      container.querySelector('button') ||
      null
    );
  };

  // Find the upsell container in the DOM. Handles both legacy
  // (#mini-atc-target) and current Section Store (ss_upsell_01) structures.
  const findMiniAtcContainer = () => {
    // Legacy structure
    const legacy = document.getElementById('mini-atc-target');
    if (legacy) return legacy;
    // Current Section Store ss_upsell_01 — the outer shopify-section
    // node has an id matching: shopify-section-template--*__ss_upsell_*
    const ssUpsell = document.querySelector(
      'div[id*="__ss_upsell"], div[id*="ss_upsell"]'
    );
    if (ssUpsell) return ssUpsell;
    // Fallback: any container holding a data-mini-add-to-cart button
    const btn = document.querySelector('[data-mini-add-to-cart]');
    return btn ? btn.closest('.shopify-section, section, div') : null;
  };

  const hijackMiniAtc = async () => {
    const container = findMiniAtcContainer();
    if (!container) return;
    if (container[MINI_ATC_FLAG]) return; // already bound on this load

    const origBtn = findMiniAtcAddButton(container);
    if (!origBtn) return; // app hasn't rendered yet — observer will re-call us

    const handle = findMiniAtcSwatchHandle(container);
    if (!handle) {
      console.warn('[SwatchTray] Mini-ATC: could not detect swatch handle');
      return;
    }

    const meta = await fetchProductMeta(handle);
    if (!meta || !meta.isSwatch || !meta.variantId) {
      // Either not a swatch or product missing — don't touch the app.
      return;
    }

    // Mark container as bound so we don't double-bind
    container[MINI_ATC_FLAG] = true;

    // ---- Step 1: clone the button to strip the app's event listeners.
    // The ss-upsell app attaches its own click handler (often via inline
    // onclick, addEventListener, or a delegated parent listener) that
    // submits to /cart/add directly. cloneNode(true) copies the node + its
    // attributes but NOT its attached event listeners — the cleanest way
    // to neutralize the app without trying to identify and removeEventListener
    // its specific handler.
    const btn = origBtn.cloneNode(true);
    origBtn.parentNode.replaceChild(btn, origBtn);

    // ---- Step 2: detach from any surrounding <form action="/cart/add">
    // so a native form submit can never happen even if a stray Enter
    // keypress reaches the button.
    btn.setAttribute('type', 'button');
    btn.removeAttribute('name'); // strip name="add" if present
    btn.removeAttribute('form');

    // Also neutralize the parent <form> if the app wrapped the button in one.
    // We don't want this form to ever submit — hijack its submit handler.
    const parentForm = btn.closest('form');
    if (parentForm && !parentForm.__swatchMiniAtcBlocked) {
      parentForm.__swatchMiniAtcBlocked = true;
      parentForm.addEventListener('submit', (e) => {
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();
      }, true);
    }

    // ---- Step 3: compute label per tier / Trade status
    const trade = isTradeCustomer();
    const cents = centsFromTier(meta.tier);
    let label;
    if (trade) {
      label = 'Add Swatch';
    } else if (cents !== null) {
      label = `Add Swatch \u2014 ${formatTierPrice(cents)}`;
    } else {
      label = 'Add Swatch';
    }
    // Replace label (clears any inner spans the app rendered)
    btn.textContent = label;

    // ---- Step 4: tag the button for our visual + a11y system
    btn.setAttribute('data-variant-id', String(meta.variantId));
    btn.setAttribute('data-tier', meta.tier);
    btn.setAttribute('data-product-handle', handle);
    btn.setAttribute('data-pop-on-add', 'true');
    btn.setAttribute('data-swatch-mini-atc', '');
    const already = inCartVariantIds.has(String(meta.variantId));
    btn.setAttribute('aria-pressed', already ? 'true' : 'false');
    btn.setAttribute('aria-label', already ? 'Remove from selection' : 'Add to selection');
    btn.classList.add('swatch-pdp-add');

    // ---- Step 5: attach OUR click handler in capture phase.
    // This MUST do the add work itself — not rely on bind()'s bubble-phase
    // handler — because capture-phase blockers stop propagation before bubble
    // listeners run. We mirror the logic from bind()'s data-tray-add handler
    // (toggle add / remove based on aria-pressed).
    const onActivate = (e) => {
      e.preventDefault();
      e.stopPropagation();
      e.stopImmediatePropagation();
      const pressed = btn.getAttribute('aria-pressed') === 'true';
      const variantId = btn.dataset.variantId;
      const tier = btn.dataset.tier || 'price-10';
      const productHandle = btn.dataset.productHandle;
      if (pressed) {
        // Already in the list — flip optimistically and remove the line.
        btn.setAttribute('aria-pressed', 'false');
        btn.setAttribute('aria-label', 'Add to selection');
        fetch('/cart.js', { credentials: 'same-origin' })
          .then((r) => r.json())
          .then((cart) => {
            const line = cart.items.find(
              (i) => String(i.variant_id) === String(variantId) && i.properties && i.properties._swatch_tier
            );
            if (line) removeFromCart(line.key, variantId);
          })
          .catch(() => {
            btn.setAttribute('aria-pressed', 'true');
            btn.setAttribute('aria-label', 'Remove from selection');
          });
      } else {
        addToCart({ variantId, tier, productHandle, popOnAdd: true });
      }
    };

    // Block ALL early phases that could otherwise reach the app's listeners
    // or trigger native submit — then do the work on click.
    const swallow = (e) => {
      e.preventDefault();
      e.stopPropagation();
      e.stopImmediatePropagation();
    };
    btn.addEventListener('mousedown', swallow, true);
    btn.addEventListener('pointerdown', swallow, true);
    btn.addEventListener('touchstart', swallow, { capture: true, passive: false });
    btn.addEventListener('click', onActivate, true);
    // Mark bound so bind() skips it (we handle it ourselves)
    btn.__swatchBound = true;
  };

  // Watch the mini-atc-target for child mutations — the app may inject
  // its content async, and may re-render when variants change.
  // We track the observer on the container itself (not module-level) so
  // soft-navigation to a different yardage PDP — which replaces the
  // container node — gets a fresh observer wired up correctly.
  const MINI_ATC_OBS_FLAG = '__swatchMiniAtcObserver';
  const watchMiniAtc = () => {
    const container = findMiniAtcContainer();
    if (!container) {
      // Container may inject later — set a body-level observer to retry
      if (!document.body.__swatchMiniAtcBodyObs) {
        const bodyObs = new MutationObserver(() => {
          const c = findMiniAtcContainer();
          if (c) {
            bodyObs.disconnect();
            document.body.__swatchMiniAtcBodyObs = null;
            watchMiniAtc();
          }
        });
        bodyObs.observe(document.body, { childList: true, subtree: true });
        document.body.__swatchMiniAtcBodyObs = bodyObs;
      }
      return;
    }
    // Try immediately in case content is already there
    hijackMiniAtc();
    if (container[MINI_ATC_OBS_FLAG]) return; // already observing this node
    const observer = new MutationObserver(() => {
      // Reset the bound flag so we re-evaluate the new button
      container[MINI_ATC_FLAG] = false;
      queueMicrotask(() => hijackMiniAtc());
    });
    observer.observe(container, { childList: true, subtree: true });
    container[MINI_ATC_OBS_FLAG] = observer;
  };

  const tryHijackMiniAtc = () => {
    try { watchMiniAtc(); } catch (e) { console.warn('[SwatchTray] Mini-ATC hijack failed', e); }
  };

  // ---- Init --------------------------------------------------------------

  // External-add listener — fires when something outside our control adds
  // a swatch (e.g. if the upsell hijack ever fails, or any future source).
  if (!document.body.__swatchExternalAddBound) {
    document.body.__swatchExternalAddBound = true;
    document.addEventListener('swatch-tray:external-add', () => {
      handleExternalAdd();
    });
  }

  const init = () => {
    try { bind(); } catch (e) { console.warn('[SwatchTray] bind failed', e); }
    // Refresh cart state first so onStickyPage() can see existing swatches.
    refreshInCartSet().finally(() => {
      try { applyVisibility(); } catch (e) { console.warn('[SwatchTray] applyVisibility failed', e); }
    });
    // Try pip injection on every page — it self-filters to swatch tiles.
    try { injectPips(); } catch (e) { console.warn('[SwatchTray] injectPips failed', e); }
    try { watchGrid(); } catch (e) { console.warn('[SwatchTray] watchGrid failed', e); }
    // Rewire PDP Add to Cart → Add Swatch (no-op if not a swatch PDP)
    try { tryRewirePdp(); } catch (e) { console.warn('[SwatchTray] tryRewirePdp failed', e); }
    // Hijack ss-upsell Mini-ATC "Add" button on yardage PDPs (no-op if card absent)
    try { tryHijackMiniAtc(); } catch (e) { console.warn('[SwatchTray] tryHijackMiniAtc failed', e); }
  };

  // Run init on a fresh microtask so we're insulated from any broken JS
  // that may be running on the same call stack (e.g. Prestige's splide.min.js
  // throwing 'parentNode of undefined' on collection pages).
  const safeInit = () => {
    Promise.resolve().then(() => {
      try { init(); } catch (e) { console.error('[SwatchTray] init failed', e); }
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', safeInit);
  } else {
    safeInit();
  }

  // Also retry after window 'load' in case Splide-style errors happened during
  // DOMContentLoaded and prevented our handlers from binding to late-rendered
  // product cards.
  window.addEventListener('load', () => {
    Promise.resolve().then(() => {
      try {
        injectPips();
        watchGrid();
        bind();
        tryRewirePdp();
        tryHijackMiniAtc();
      } catch (e) { console.warn('[SwatchTray] post-load reinit failed', e); }
    });
  });

  // ---- Client-side navigation hook --------------------------------------
  //
  // Prestige uses client-side navigation for filter changes, sort changes,
  // pagination, and filter clearing — it swaps DOM via AJAX without a full
  // page load. The URL updates via history.pushState / replaceState.
  //
  // We monkey-patch those history methods to fire a 'urlchange' event, then
  // re-run pip injection on every URL change. This is the most resilient
  // hook because it doesn't depend on knowing which DOM nodes Prestige
  // touches — if the URL changed, the grid likely re-rendered, so we
  // re-scan and inject. injectPips() is idempotent (skips already-bound
  // cards) and self-filters to swatch tiles, so this is safe everywhere.
  //
  // We also retry a few times with backoff because the AJAX response may
  // not have written the new tiles yet at the moment history.pushState fires.
  (function patchHistory() {
    if (window.__swatchTrayHistoryPatched) return;
    window.__swatchTrayHistoryPatched = true;
    const fire = () => {
      try { window.dispatchEvent(new Event('swatchtray:urlchange')); } catch (e) { /* noop */ }
    };
    const origPush = history.pushState;
    const origReplace = history.replaceState;
    history.pushState = function () {
      const r = origPush.apply(this, arguments);
      fire();
      return r;
    };
    history.replaceState = function () {
      const r = origReplace.apply(this, arguments);
      fire();
      return r;
    };
    window.addEventListener('popstate', fire);
  })();

  const reinjectAfterNav = () => {
    // Reset bound flags on any product-cards that may have been swapped in.
    // (Cards we already injected pips onto will still have .swatch-pip
    // children, so the injectPips() double-inject guard catches them.)
    $$('product-card[handle]').forEach((c) => {
      // If the card was swapped in fresh, it won't have a pip yet — clear
      // the bound flag so we re-evaluate it.
      const figure = c.querySelector('.product-card__figure') || c;
      if (!figure.querySelector('.swatch-pip')) {
        c.__swatchPipBound = false;
      }
    });
    try { injectPips(); } catch (e) { /* noop */ }
    try { watchGrid(); } catch (e) { /* noop */ }
    // If we soft-navigated to a swatch PDP, rewire it.
    // Clear rewire flag so a different handle gets re-evaluated.
    const currentHandle = pdpHandleFromUrl();
    if (document.body.dataset[PDP_REWIRE_FLAG] && document.body.dataset[PDP_REWIRE_FLAG] !== currentHandle) {
      delete document.body.dataset[PDP_REWIRE_FLAG];
    }
    try { tryRewirePdp(); } catch (e) { /* noop */ }
    // Yardage PDPs may also be soft-navigated to — re-hijack the Mini-ATC card.
    try { tryHijackMiniAtc(); } catch (e) { /* noop */ }
  };

  window.addEventListener('swatchtray:urlchange', () => {
    // The AJAX response may not have populated tiles yet. Retry on a few
    // animation frames + a fallback timeout to cover slow networks.
    let tries = 0;
    const maxTries = 8;
    const attempt = () => {
      tries += 1;
      reinjectAfterNav();
      if (tries < maxTries) {
        // Try again on the next frame to catch late-rendered tiles
        requestAnimationFrame(attempt);
      }
    };
    requestAnimationFrame(attempt);
    // Final safety net: one more pass at 1.5s in case AJAX is slow
    setTimeout(reinjectAfterNav, 1500);
  });

  // After every tray refresh, re-sync pip pressed-states across the grid
  window.__syncPipStates = async () => {
    await refreshInCartSet();
    // Cart change may flip whether the page is "sticky".
    invalidateStickyCache();
    $$('.swatch-pip[data-variant-id]').forEach((pip) => {
      const inCart = inCartVariantIds.has(String(pip.dataset.variantId));
      pip.setAttribute('aria-pressed', inCart ? 'true' : 'false');
      pip.setAttribute('aria-label', inCart ? 'Remove from selection' : 'Add to selection');
    });
  };
  const syncPipStates = window.__syncPipStates;

  // Expose a tiny API for debugging or external triggers (e.g. quickview)
  window.SwatchTray = {
    refresh: async () => { await refreshTray(); await window.__syncPipStates(); },
    open: togglePillToTray,
    add: addToCart,
    injectPips,
  };
})();
